import angular from 'angular';
import csmCommonWeb from 'csm-common-web';
export default angular
  .module('books.util.ProductUtils', [csmCommonWeb, 'ui.grid'])
  /*******************************************************************************
   * UiGrid Class Extensions
   *******************************************************************************/
  .run(function (Grid, GridColumn) {
    /*------------------------------------------------*/
    /* Extend GridColumn object to set grid reference */
    /* on associated column definition object         */
    /*------------------------------------------------*/
    var mUpdateGC = GridColumn.prototype.updateColumnDef;
    GridColumn.prototype.updateColumnDef = function (colDef, isNew) {
      mUpdateGC.call(this, colDef, isNew);
      colDef.grid = this.grid;
    };
  })
  .factory('InventoryOptionsColumn', InventoryOptionsColumnFactory).name;

/*******************************************************************************
 * Factory: ProductUtils
 *
 * Contains utilities for working with product objects
 *******************************************************************************/
/* @ngInject */
function InventoryOptionsColumnFactory(Tools, uiGridConstants, Grid) {
  /** Option Separator. */ var OPTION_SEPARATOR = ' | ';

  /*---------------------------------------*/
  /* Extend the sortColumn routine to      */
  /* update InventoryOptionsColumn objects */
  /*---------------------------------------*/
  var mGridSortColumn = Grid.prototype.sortColumn;
  Grid.prototype.sortColumn = function (column, directionOrAdd, add) {
    /*--------------------*/
    /* Set add if not set */
    /*--------------------*/
    if (add == undefined) add = directionOrAdd;

    /*--------------------------------------------------------*/
    /* Update sort when matched and always use sort ascending */
    /*--------------------------------------------------------*/
    if (column && column.colDef.constructor.name == 'InventoryOptionsColumn') {
      column.colDef.updateSort();
      directionOrAdd = uiGridConstants.ASC;
    }

    return mGridSortColumn.call(this, column, directionOrAdd, add);
  };

  /*****************************************************************************
   * Class: InventoryOptionsColumn
   *
   * Creates an column definition object to be used with uiGrid and adds some
   * additional methods to rearrange item option values based on a provided
   * list of ItemOptionMaster items.
   *****************************************************************************/
  function InventoryOptionsColumn() {
    /** Self Reference. */
    var self = this;

    /** Multidimensional array of master option permutations. */
    var mOptionPermutations = [];

    /** Current Permutation Index. */
    var mPermIndex = 0;

    /** Getter for the GridColumn reference. */
    var gridColumn = function () {
      return self.grid
        ? self.grid.columns
            .filter(function (c) {
              return c.colDef == self;
            })
            .shift()
        : null;
    };

    /** Getter for the current master options permutation. */
    var optionHeaderList = function () {
      return mOptionPermutations.length > 0
        ? mOptionPermutations[mPermIndex % mOptionPermutations.length]
        : [];
    };

    /***************************************************************************
     * syncOptions */
    /**
     * Updates the options property on the provided <b>InventoryProduct</b>
     * object so the option item order corresponds to the current master item
     * permutation.
     *
     * @param invProduct  Inventory Product object to update
     ***************************************************************************/
    var syncOptions = function (invProduct) {
      var newOptions = [];

      /*-------------------------------------------------------*/
      /* Match the values in the order of the masterValues and */
      /* add them to the newOptions list in that same order.   */
      /*-------------------------------------------------------*/
      optionHeaderList().forEach(function (master) {
        invProduct.options.forEach(function (invOption) {
          if (master.id == invOption.masterOptionID) newOptions.push(invOption);
        });
      });

      invProduct.options = newOptions;
    };

    /***************************************************************************
     * updateDisplayName */
    /**
     * Updates the displayName property to be the concatentation of the current
     * permutation's option order.
     ***************************************************************************/
    var updateDisplayName = function () {
      self.displayName = optionHeaderList()
        .map(function (o) {
          return o.name;
        })
        .join(OPTION_SEPARATOR);

      var col = gridColumn();
      if (col) col.updateColumnDef(self, false);
    };

    /***************************************************************************
     * Utility Functions
     ***************************************************************************/
    /***************************************************************************
     * setMasterOptions */
    /**
     * Set the list of ItemOptionMaster objects to use with the column. This will
     * generate all available permutations for the objects.
     *
     * @param  masterList  Array of ItemOptionMaster objects to work with
     ***************************************************************************/
    self.setMasterOptions = function (masterList) {
      mOptionPermutations = masterList
        ? Tools.generatePermutations(masterList)
        : [];
      updateDisplayName();
    };

    /***************************************************************************
     * updateSort */
    /**
     * Update the permutation index so the next permutation will be used for
     * sorting and header display. The inventory product objects will have their
     * options synchronized with the current master order.
     ***************************************************************************/
    self.updateSort = function () {
      mPermIndex++;
      updateDisplayName();

      /*------------------------------------------*/
      /* Resort the options for each Inventory    */
      /* Product option to match the header sort. */
      /*------------------------------------------*/
      for (var idx = 0; idx < self.grid.rows.length; idx++)
        syncOptions(self.grid.rows[idx].entity);
    };

    /*------------------------------*/
    /* Column Definition Properties */
    /*------------------------------*/
    self.name = 'Options';
    self.enableCellEdit = false;
    self.sortDirectionCycle = [uiGridConstants.ASC];
    self.labelFunction = function (inventoryProduct) {
      return InventoryOptionsColumn.optionsLabelFunction(
        inventoryProduct.options
      );
    };

    /***************************************************************************
     * sortFunc */
    /**
     * Custom sort function to sort by the first element of the <i>options</i>
     * arrays. Each time we sort, the order of the elements will be different
     * option.
     *
     * @param  a     Cell 1 Text
     * @param  b     Cell 2 Text
     * @param  row1  First row object to compare.
     * @param  row2  Second row object to compare.
     * @return       Integer-based comparison. -1=<b>product1</b>,
     *                 1=<b>product2</b>, 0=Same.
     ***************************************************************************/
    self.sortingAlgorithm = function (a, b, row1, row2) {
      var product1 = row1.entity;
      var product2 = row2.entity;

      /*--------------------------------------------------*/
      /* If we don't have either product, or there are no */
      /* options, then consider them as the same product. */
      /*--------------------------------------------------*/
      if (
        !product1 ||
        product1.options.length == 0 ||
        !product2 ||
        product2.options.length == 0
      )
        return 0;

      /*-------------------------------------------------------------*/
      /* Use the first InventoryProductOption to perform comparisons */
      /*-------------------------------------------------------------*/
      var option1 = product1.options[0];
      var option2 = product2.options[0];

      return option1.itemOptionValue.toLowerCase() <
        option2.itemOptionValue.toLowerCase()
        ? -1
        : option1.itemOptionValue.toLowerCase() >
            option2.itemOptionValue.toLowerCase()
          ? 1
          : 0;
    };
  }

  /*****************************************************************************
   * optionsLabelFunction */
  /**
   * Returns a display string for the provided array of <b>InventoryProductOptions</b>
   * by joining their option values with the OPTION_SEPARATOR.
   *
   * @param  inventoryProductOptions Array of options to use
   * @return                         Display string
   *****************************************************************************/
  InventoryOptionsColumn.optionsLabelFunction = function (
    inventoryProductOptions
  ) {
    if (!inventoryProductOptions) return '';

    return inventoryProductOptions
      .map(function (o) {
        return o.itemOptionValue;
      })
      .join(OPTION_SEPARATOR);
  };

  return InventoryOptionsColumn;
}
